import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "./Toaster";
import { log } from "../utils/logger";

export default function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, authReady } = useAuth();
  const location = useLocation();
  const { show } = useToast();

  useEffect(() => {
    if (authReady && user && !isAdmin) {
      log.warn('admin_access_denied', {
        message: `User attempted to access admin page without privileges: ${location.pathname}`,
        path: location.pathname
      });
      show({ 
        title: 'Access Denied', 
        description: 'Administrator privileges are required to access this page.',
        type: 'error'
      });
    }
  }, [authReady, user, isAdmin, show, location.pathname]);

  // Still loading authentication state
  if (!authReady) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Not logged in - redirect to login
  if (!user) {
    const dest = `/login${location.search}`;
    return <Navigate to={dest} state={{ from: location.pathname + location.search }} replace />;
  }

  // Logged in but not admin - show access denied
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-8">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600 mb-6">
            Administrator privileges are required to access this page.
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => window.history.back()}
              className="px-6 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition"
            >
              Go Back
            </button>
            <a
              href="/"
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
            >
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

